import { useEffect, useMemo, useRef, useState } from 'react'
import type { UIEvent } from 'react'
import type { MarketEvent } from '../../api/client'
import { STATUS_TEXTE, artText, statusTon } from '../../pages/eventVocabulary'
import { monat, quartal, tagKurz, tagKuerzer, ungenau, wochentag, zeitraum }
  from './ereignisZeit'
import s from './EreignisListe.module.scss'

/**
 * Eine Gruppe der Liste: ein Tag, eine Periode ohne festen Tag, oder das,
 * was gar keinen Termin hat.
 */
export interface EreignisGruppe {
  /** Der Tag als JJJJ-MM-TT, `M2026-09`, `Q2026-4` oder `offen`. */
  key: string
  art: 'tag' | 'periode' | 'offen'
  /** Der Tag, wenn es einer ist — für die Markierung „heute". */
  tag: string | null
  /** „06.09.2026", „September 2026", „Q4 2026", „Ohne Termin". */
  datum: string
  /** „Samstag", „ohne festen Tag", „Zeitpunkt noch nicht angesetzt". */
  neben: string
  eintraege: MarketEvent[]
  /** Mehrtägiges, das an diesem Tag läuft, aber früher begann. */
  laufend: MarketEvent[]
}

/** Der Schlüssel der Periode — oder keiner, wenn das Ereignis auf einen Tag gehört. */
function periode(e: MarketEvent): string | null {
  if (!e.date_from) return null
  if (e.date_precision === 'MONTH' || e.date_precision === 'ESTIMATED') {
    return `M${e.date_from.slice(0, 7)}`
  }
  if (e.date_precision === 'QUARTER') {
    const d = new Date(e.date_from.slice(0, 4) + '-01-01')
    const q = Math.floor((Number(e.date_from.slice(5, 7)) - 1) / 3) + 1
    return `Q${d.getFullYear()}-${q}`
  }
  return null
}

/**
 * Nach Tagen gruppiert, wie ein Kalender.
 *
 * TAGE zuerst, aufsteigend: Was auf einen Tag genau steht (EXACT, und
 * Mehrtägiges an seinem ersten Tag). Ein mehrtägiges Ereignis erscheint an
 * jedem weiteren Tag seiner Spanne, an dem ohnehin etwas steht, als „läuft".
 * Dann die PERIODEN — Monat oder Quartal, für Termine, deren Tag noch nicht
 * feststeht; Schätzungen gehören in ihren Monat. Zuletzt, was gar keinen
 * Termin hat.
 */
export function gruppiereTage(items: MarketEvent[]): EreignisGruppe[] {
  const tage = new Map<string, MarketEvent[]>()
  const perioden = new Map<string, MarketEvent[]>()
  const offen: MarketEvent[] = []
  for (const e of items) {
    if (!e.date_from) { offen.push(e); continue }
    const p = periode(e)
    if (p) { perioden.set(p, [...(perioden.get(p) ?? []), e]); continue }
    tage.set(e.date_from, [...(tage.get(e.date_from) ?? []), e])
  }
  const spannen = items.filter((e) =>
    e.date_precision === 'DATE_RANGE' && e.date_from && e.date_to)

  const tagGruppen: EreignisGruppe[] = [...tage.keys()].sort().map((tag) => ({
    key: tag, art: 'tag', tag, datum: tagKurz(tag), neben: wochentag(tag),
    eintraege: tage.get(tag)!,
    laufend: spannen.filter((e) => e.date_from! < tag && e.date_to! >= tag),
  }))
  const periodenGruppen: EreignisGruppe[] = [...perioden.keys()].sort().map((key) => {
    const erste = perioden.get(key)![0]
    return {
      key, art: 'periode', tag: null,
      datum: key.startsWith('Q') ? quartal(erste.date_from!) : monat(erste.date_from!),
      neben: 'ohne festen Tag', eintraege: perioden.get(key)!, laufend: [],
    }
  })
  const rest: EreignisGruppe[] = offen.length ? [{
    key: 'offen', art: 'offen', tag: null, datum: 'Ohne Termin',
    neben: 'Zeitpunkt noch nicht angesetzt', eintraege: offen, laufend: [],
  }] : []
  return [...tagGruppen, ...periodenGruppen, ...rest]
}

/**
 * Die Ereignisliste — der linke Teil der Master-Detail-Ansicht.
 *
 * Immer nach Tagen gruppiert: Ein Kalender ist keine Liste. Je Zeile Art,
 * Genauigkeit (wenn der Termin nicht auf den Tag steht), Status, Titel und
 * die Ziele als Chips. Der heutige Tag trägt eine Marke und wird beim Laden
 * angesprungen. Die Liste scrollt in sich; am Ende ruft sie nach.
 */
export function EreignisListe({ items, gewaehlt, onWaehle, heute, onEndReached,
                                loadingMore, leer, fuss }: {
  items: MarketEvent[]
  gewaehlt: number | null
  onWaehle: (id: number) => void
  /** Der Tag, der „hier stehen wir" markiert — heute oder der nächste mit Terminen. */
  heute?: string | null
  onEndReached?: () => void
  loadingMore?: boolean
  /** Was bei leerer Liste steht. */
  leer: string
  /** Die Zeile unter der Liste, etwa „30 Ereignisse". */
  fuss?: string
}) {
  const [zu, setZu] = useState<Set<string>>(new Set())
  const gruppen = useMemo(() => gruppiereTage(items), [items])
  const rollen = useRef<HTMLDivElement>(null)
  const heuteKopf = useRef<HTMLButtonElement>(null)

  // EINMAL zum heutigen Tag springen, sobald er in der Liste steht. Innerhalb
  // des Kastens, nicht die Seite: Der Kasten scrollt, die Seite nicht.
  const gesprungen = useRef<string | null>(null)
  useEffect(() => {
    const kasten = rollen.current, kopf = heuteKopf.current
    if (!heute || !kasten || !kopf || gesprungen.current === heute) return
    gesprungen.current = heute
    kasten.scrollTop = kopf.offsetTop - kasten.offsetTop
  }, [heute, gruppen])

  function onScroll(event: UIEvent<HTMLDivElement>) {
    const el = event.currentTarget
    if (onEndReached && el.scrollTop + el.clientHeight >= el.scrollHeight - 120) onEndReached()
  }

  return (
    <div className={s.liste}>
      <div className={s.rollen} onScroll={onScroll} ref={rollen}>
        {items.length === 0 && <div className={s.leer}>{leer}</div>}

        {gruppen.map((gruppe) => {
          const offen = !zu.has(gruppe.key)
          const istHeute = gruppe.tag !== null && gruppe.tag === heute
          return (
            <div key={gruppe.key}>
              <button type="button" className={s.gruppe} aria-expanded={offen}
                      data-heute={istHeute || undefined}
                      ref={istHeute ? heuteKopf : undefined}
                      onClick={() => setZu((jetzt) => {
                        const naechste = new Set(jetzt)
                        if (naechste.has(gruppe.key)) naechste.delete(gruppe.key)
                        else naechste.add(gruppe.key)
                        return naechste
                      })}>
                <span className={s.pfeil} aria-hidden="true">{offen ? '▾' : '▸'}</span>
                <span className={s.gruppeDatum}>{gruppe.datum}</span>
                <span className={s.gruppeNeben}>{gruppe.neben}</span>
                {istHeute && <span className={s.heute}>heute</span>}
                <span className={s.gruppeAnzahl}>{gruppe.eintraege.length}</span>
              </button>
              {gruppe.laufend.map((e) => (
                <button key={`laufend-${e.id}`} type="button" className={s.laufend}
                        onClick={() => onWaehle(e.id)}
                        title={`${zeitraum(e)} — läuft an diesem Tag`}>
                  <span className={s.strich} aria-hidden="true" />
                  <span className={s.laeuft}>läuft</span>
                  <span className={s.laufendText}>{artText(e)} · {e.title}</span>
                  <span className={s.laufendBis}>bis {tagKuerzer(e.date_to!)}</span>
                </button>
              ))}
              {offen && gruppe.eintraege.map((e) => (
                <Zeile key={e.id} item={e} aktiv={e.id === gewaehlt}
                       onClick={() => onWaehle(e.id)} />
              ))}
            </div>
          )
        })}

        {loadingMore && <div className={s.nachladen}>lädt weitere Ereignisse …</div>}
      </div>
      {fuss && (
        <div className={s.fuss}>
          <span>{fuss}</span>
          <span title="Pfeiltasten wechseln das Ereignis">↑↓ wechseln</span>
        </div>
      )}
    </div>
  )
}

function Zeile({ item, aktiv, onClick }: {
  item: MarketEvent
  aktiv: boolean
  onClick: () => void
}) {
  return (
    <button type="button" className={s.zeile} data-aktiv={aktiv || undefined}
            onClick={onClick} aria-current={aktiv ? 'true' : undefined}>
      <span className={s.kopf}>
        <span className={s.art}>{artText(item)}</span>
        {ungenau(item) && <span className={s.zeitraum}>{zeitraum(item)}</span>}
        <span className={s.status} data-ton={statusTon(item.status)}>
          {STATUS_TEXTE[item.status] ?? item.status}
        </span>
      </span>
      <span className={s.titel}>{item.title}</span>
      <span className={s.chips}>
        {item.targets.length === 0
          ? <span className={s.chip} data-markt>Markt</span>
          : item.targets.map((z, i) => (
              <span key={`${z.ref}-${i}`} className={s.chip} data-offen={z.name === null || undefined}>
                {z.name ?? z.ref}
              </span>
            ))}
      </span>
    </button>
  )
}
